import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import styles from './Product.styles';
import { showAddressModal, showMessageModal } from '../../../../redux/actions';

import { UNIT } from '../../../../constants/configuration';

import { displayPriceFormat, getNbInCartWithKey, isChatbot, isProductDisabled, isQrCode, } from '../../../../utils';
import ProductModal from './components/ProductModal';
import { sendGoogleAnalytics } from '../../../../api';
import { CUSTOMER_ADDRESS_CHECK_VALUES } from '../../../../redux/reducers/componentReducer';
import { isCustomerAddressDefined } from '../../../../utils/customer-address-and-shops';
import { getActiveOptions, getCurrentOfferForProduct, getOfferProductsNbInPendingOrderBasket, } from './Product.services';
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import ProductCard from './components/ProductCard/ProductCard';
import { analyticsEvents } from '../../../../utils/firebaseAnalytics';
import ProductOfferTooltip from './components/ProductOfferTooltip/ProductOfferTooltip';
import { isObjectDefined } from "../../../../utils/object";

type ProductProps = {
  product: any;
  title: string;
  cardImage: any;
  price: any;
  description: string;
  productKey: string;
  sectionKey: string;
  isOfferSection: boolean;
  messagesSectionRange: any;
};

export default function Product({
  product,
  title,
  cardImage,
  price,
  description,
  productKey,
  sectionKey,
  isOfferSection,
  messagesSectionRange,
}: ProductProps) {
  const { name, key, position } = product;
  const { sections } = useAppSelector((state) => state.shopReducer);
  const section = _.find(sections, (section) => section.key === sectionKey);
  const dispatch = useAppDispatch();

  const [showModal, setShowModal] = useState(false);
  const [cartNb, setCartNb] = useState(0);
  const [categories, setCategories] = useState([]);
  const [productOffer, setProductOffer] = useState(null);

  const { productOffers, thresholdOffer } = useAppSelector(
    (state) => state.offerReducer
  );

  const { secondaryColor, multiOrder, generalDesign } = useAppSelector(
    (state) => state.configurationReducer
  );

  const { customerAddressCheck } = useAppSelector(
    (state) => state.componentReducer
  );
  const { orders } = useAppSelector((state) => state.orderReducer);
  const { commande, address: customerAddress, totalProductPriceWithPromo = 0 } = useAppSelector(
    (state) => state.pendingOrderReducer
  );
  const { categories: shopCategories } = useAppSelector(
    (state) => state.shopReducer
  );

  useEffect(() => {
    if (!_.isEmpty(commande)) {
      const { key } = product;
      const cartNb = getNbInCartWithKey(key, productOffer);
      setCartNb(cartNb);
    } else {
      setCartNb(0);
    }
  }, [commande, productOffer]);

  const hideModal = () => {
    setShowModal(false);
    updateCategories();
  };

  const productCardStyle = _.clone(styles.productCard);
  if (cartNb > 0) {
    productCardStyle['border'] = `2px solid ${secondaryColor}`;
  }
  const potentialThreshold = productOffer?.config?.threshold;
  const thresholdNotReached = isObjectDefined(potentialThreshold) && totalProductPriceWithPromo < potentialThreshold;
  const potentialLimit = productOffer?.config?.limit;
  const limitReached = isObjectDefined(potentialLimit) && Number(potentialLimit) <= getOfferProductsNbInPendingOrderBasket(commande, productOffer?.key)
  const isDisabled = isProductDisabled(product) || messagesSectionRange?.[section?.key];
  const productDisabled =
    isDisabled || thresholdNotReached || limitReached;
  const hideProductName =
    section?.hideProductName || generalDesign?.hideProductsName?.products;

  const updateCategories = useCallback(() => {
    const tempCate = getActiveOptions(product.categories, shopCategories);
    if (
      productOffer &&
      shopCategories[`${productOffer?.key}__${sectionKey}__${productKey}`]
    ) {
      tempCate.push(
        `${productOffer?.key}__${sectionKey}__${productKey}@${tempCate.length}`
      );
    }
    setCategories(tempCate);
  }, [product.categories, shopCategories, productOffer]);

  useEffect(() => {
    updateCategories();
  }, [updateCategories]);

  useEffect(() => {
    setProductOffer(
      getCurrentOfferForProduct(
        productKey,
        section?.key,
        thresholdOffer,
        productOffers,
        isOfferSection
      )
    );
  }, [productKey, section?.key, thresholdOffer, productOffers, isOfferSection]);

  const handleClickCard = () => {
    if (!productDisabled) {
      if (
        !isChatbot() &&
        !isQrCode() &&
        (customerAddressCheck !== CUSTOMER_ADDRESS_CHECK_VALUES.VALID ||
          !isCustomerAddressDefined(customerAddress))
      ) {
        dispatch(showAddressModal(true));
      } else if (!_.isEmpty(orders) && !multiOrder) {
        dispatch(
          showMessageModal(
            `Vous ne pouvez pas ajouter de produits car vous avez déjà ${_.size(orders) > 1
              ? `${_.size(orders)} commandes`
              : 'une commande'
            } en cours`
          )
        );
      } else {
        sendGoogleAnalytics('view_item', {
          items: [
            {
              id: key,
              name,
              price,
              position,
              category: section.name,
            },
          ],
          currency: 'EUR',
          value: price,
        });
        analyticsEvents.logViewItem(
          'productView',
          key,
          name,
          price,
          section.name,
          position,
          'EUR'
        );
        setShowModal(true);
      }
    }
  };

  const getDisabledMessage = () => {
    if (isDisabled) {
      return 'Produit Indisponible';
    }
    if (limitReached) {
      return `Offre limitée à ${potentialLimit} article${Number(potentialLimit) > 1 ? 's' : ''}`;
    }
    if (thresholdNotReached) {
    return `Offre disponible dès ${displayPriceFormat(potentialThreshold, false, true)} d’achat`;
    }
  };

  return (
    <>
      <div
        className={`card text-center my-2 my-sm-3 productCard`}
        style={productCardStyle}
        onClick={handleClickCard}
      >
        {productOffer && <ProductOfferTooltip offer={productOffer} />}
        {cartNb > 0 && (
          <div
            style={{
              position: 'absolute',
              height: '30px',
              width: '30px',
              right: -10,
              bottom: -9,
              zIndex: 1000,
              borderRadius: UNIT * 2,
              backgroundColor: secondaryColor,
            }}
          >
            <div
              className='text-white d-flex align-items-center justify-content-center'
              style={{
                height: '100%',
                fontSize: UNIT * 1.25,
              }}
            >
              {cartNb}
            </div>
          </div>
        )}

        {productDisabled && (
          <div
            className='overlay'
            style={{ borderRadius: UNIT }}
          >
            <div className='text-overlay'>
              <h6 className='text-white disabledTitle mb-0 font-weight-bold'>
                {getDisabledMessage()}
              </h6>
            </div>
          </div>
        )}
        <ProductCard
          title={title}
          description={description}
          image={cardImage}
          product={{ ...product, sectionKey }}
          hideProductName={hideProductName}
          productOffer={productOffer}
        />
      </div>
      {showModal && (
        <div className='mt-2 mt-sm-3'>
          <ProductModal
            product={product}
            productOffer={productOffer}
            title={product.name}
            image={product.image}
            price={product.price}
            description={product.description}
            categories={categories}
            sectionName={section.name}
            showModal={showModal}
            productKey={productKey}
            sectionKey={section?.key}
            onHideModal={() => hideModal()}
          />
        </div>
      )}
    </>
  );
}
